import { THREE, OrbitControls } from "./vendor/three";
import type { AppState, Parameters, QualityName } from "./state";
import { QUALITY_PROFILES } from "./state";
import fullscreenVertex from "./shaders/fullscreen.vert.glsl?raw";
import raytraceFragment from "./shaders/raytrace.frag.glsl?raw";
import brightFragment from "./shaders/bright.frag.glsl?raw";
import blurFragment from "./shaders/blur.frag.glsl?raw";
import compositeFragment from "./shaders/composite.frag.glsl?raw";

export interface RenderStats {
  width: number;
  height: number;
  dpr: number;
  renderScale: number;
  hdr: boolean;
  webgl2: boolean;
  renderer: string;
}

interface CameraTween {
  start: any;
  end: any;
  startTime: number;
  duration: number;
  preset: string;
}

const PRESETS: Record<string, [number, number, number]> = {
  observer: [14.1, 6.2, 9.2],
  "photon-ring": [9.8, 1.05, 1.2],
  polar: [4.8, 21.2, 2.5],
  "cinematic-wide": [28.5, 14.4, 11.2]
};

function makeTarget(width: number, height: number, type: any): any {
  const target = new THREE.WebGLRenderTarget(width, height, {
    type,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
    stencilBuffer: false
  });
  target.texture.generateMipmaps = false;
  target.texture.colorSpace = THREE.LinearSRGBColorSpace;
  return target;
}

export class GargantuaRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly camera: any;
  readonly controls: any;
  readonly renderer: any;
  onInteraction?: () => void;
  onContextState?: (message: string | null) => void;
  onShaderError?: (message: string) => void;

  private readonly quadScene: any;
  private readonly quadCamera: any;
  private readonly quad: any;
  private readonly rayMaterial: any;
  private readonly brightMaterial: any;
  private readonly blurMaterial: any;
  private readonly compositeMaterial: any;
  private sceneTarget: any;
  private brightTarget: any;
  private blurTarget: any;
  private bloomTarget: any;
  private readonly targetType: any;
  private width = 1;
  private height = 1;
  private cssWidth = 1;
  private cssHeight = 1;
  private dpr = 1;
  private renderScale = 1;
  private quality: QualityName = "high";
  private cameraTween: CameraTween | null = null;
  private cinematicCurve: any;
  private cinematicStartedAt = performance.now();
  private cinematicSuspendUntil = 0;
  private shaderFailure = "";

  constructor(canvas: HTMLCanvasElement, state: AppState) {
    this.canvas = canvas;
    const context = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false
    });
    if (!context) throw new Error("WebGL 2 is required but unavailable.");

    this.renderer = new THREE.WebGLRenderer({ canvas, context, antialias: false, alpha: false });
    this.renderer.setClearColor(0x010207, 1);
    this.renderer.setPixelRatio(1);
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer.debug.checkShaderErrors = true;
    if (this.renderer.debug) {
      this.renderer.debug.onShaderError = (_gl: unknown, _program: unknown, _vs: unknown, _fs: unknown) => {
        this.shaderFailure = "GLSL shader compilation or link failure.";
        this.onShaderError?.(this.shaderFailure);
      };
    }

    this.camera = new THREE.PerspectiveCamera(state.parameters.cameraFov, 1, 0.01, 500);
    this.camera.position.set(...(PRESETS[state.preset] ?? PRESETS.observer!));
    this.camera.lookAt(0, 0, 0);
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.target.set(0, 0, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.055;
    this.controls.minDistance = 6.5;
    this.controls.maxDistance = 60;
    this.controls.enablePan = false;
    this.controls.addEventListener("start", () => {
      this.cinematicSuspendUntil = performance.now() + 12_000;
      this.cameraTween = null;
      this.onInteraction?.();
    });
    this.controls.addEventListener("end", () => this.onInteraction?.());

    this.quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute([-1,-1,0, 3,-1,0, -1,3,0], 3));
    this.quadScene = new THREE.Scene();

    this.rayMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVertex,
      fragmentShader: raytraceFragment,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uResolution: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 }, uSeed: { value: state.seed },
        uCameraPosition: { value: new THREE.Vector3() },
        uCameraForward: { value: new THREE.Vector3() },
        uCameraRight: { value: new THREE.Vector3() },
        uCameraUp: { value: new THREE.Vector3() },
        uFov: { value: state.parameters.cameraFov },
        uStepSize: { value: state.parameters.integrationStep },
        uMaximumSteps: { value: state.parameters.maximumSteps },
        uDiskInner: { value: state.parameters.diskInnerRadius },
        uDiskOuter: { value: state.parameters.diskOuterRadius },
        uDiskTemperature: { value: state.parameters.diskTemperature },
        uEmissivityFalloff: { value: state.parameters.emissivityFalloff },
        uAccretionRate: { value: state.parameters.accretionRate },
        uTurbulenceScale: { value: state.parameters.turbulenceScale },
        uTurbulenceStrength: { value: state.parameters.turbulenceStrength },
        uDiskRotationSpeed: { value: state.parameters.diskRotationSpeed },
        uStarDensity: { value: state.parameters.starDensity },
        uGalaxyIntensity: { value: state.parameters.galaxyIntensity },
        uDebugView: { value: state.debugView }
      }
    });
    this.brightMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3, vertexShader: fullscreenVertex, fragmentShader: brightFragment,
      depthTest: false, depthWrite: false,
      uniforms: { uInput: { value: null }, uThreshold: { value: state.parameters.bloomThreshold } }
    });
    this.blurMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3, vertexShader: fullscreenVertex, fragmentShader: blurFragment,
      depthTest: false, depthWrite: false,
      uniforms: { uInput: { value: null }, uDirection: { value: new THREE.Vector2() }, uRadius: { value: state.parameters.bloomRadius } }
    });
    this.compositeMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3, vertexShader: fullscreenVertex, fragmentShader: compositeFragment,
      depthTest: false, depthWrite: false,
      uniforms: {
        uScene: { value: null }, uBloom: { value: null }, uResolution: { value: new THREE.Vector2(1,1) },
        uExposure: { value: state.parameters.exposure },
        uBloomStrength: { value: state.parameters.bloomStrength },
        uChromatic: { value: state.parameters.chromaticAberration },
        uVignette: { value: state.parameters.vignette },
        uGrain: { value: state.parameters.filmGrain },
        uTime: { value: 0 }
      }
    });
    this.quad = new THREE.Mesh(geometry, this.rayMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    const gl = this.renderer.getContext();
    const hdr = Boolean(gl.getExtension("EXT_color_buffer_float"));
    this.targetType = hdr ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this.sceneTarget = makeTarget(1, 1, this.targetType);
    this.brightTarget = makeTarget(1, 1, this.targetType);
    this.blurTarget = makeTarget(1, 1, this.targetType);
    this.bloomTarget = makeTarget(1, 1, this.targetType);

    this.cinematicCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(14.1, 6.2, 9.2), new THREE.Vector3(9.5, 1.4, 2.0),
      new THREE.Vector3(-13.0, 3.4, 9.0), new THREE.Vector3(-22.0, 13.0, -8.0),
      new THREE.Vector3(6.0, 25.0, -6.0), new THREE.Vector3(28.5, 14.4, 11.2)
    ], true, "catmullrom", 0.25);

    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.onContextState?.("WebGL context lost. Waiting for recovery…");
    });
    canvas.addEventListener("webglcontextrestored", () => {
      this.renderer.resetState?.();
      this.resize(this.cssWidth, this.cssHeight, this.quality, this.renderScale);
      this.onContextState?.(null);
    });

    this.setQuality(state.quality);
    this.updateParameters(state.parameters);
    this.setDebugView(state.debugView);
    this.renderer.compile(this.quadScene, this.quadCamera);
  }

  dispose(): void {
    this.controls.dispose();
    this.rayMaterial.dispose(); this.brightMaterial.dispose(); this.blurMaterial.dispose(); this.compositeMaterial.dispose();
    this.sceneTarget.dispose(); this.brightTarget.dispose(); this.blurTarget.dispose(); this.bloomTarget.dispose();
    this.quad.geometry.dispose();
    this.renderer.dispose();
  }

  setQuality(quality: QualityName): void {
    this.quality = quality;
    this.renderScale = QUALITY_PROFILES[quality].scale;
    this.resize(innerWidth, innerHeight, quality, this.renderScale);
  }

  setRenderScale(scale: number): void {
    const profile = QUALITY_PROFILES[this.quality];
    const next = Math.min(profile.scale, Math.max(profile.minScale, scale));
    if (Math.abs(next - this.renderScale) > 0.01) this.resize(this.cssWidth, this.cssHeight, this.quality, next);
  }

  getRenderScale(): number { return this.renderScale; }

  resize(cssWidth: number, cssHeight: number, quality = this.quality, scale = this.renderScale): void {
    const profile = QUALITY_PROFILES[quality];
    this.cssWidth = Math.max(1, cssWidth);
    this.cssHeight = Math.max(1, cssHeight);
    this.dpr = Math.min(devicePixelRatio || 1, profile.dprCap);
    this.renderScale = Math.min(profile.scale, Math.max(profile.minScale, scale));
    this.width = Math.max(2, Math.round(this.cssWidth * this.dpr * this.renderScale));
    this.height = Math.max(2, Math.round(this.cssHeight * this.dpr * this.renderScale));
    this.renderer.setSize(this.width, this.height, false);
    this.canvas.style.width = `${this.cssWidth}px`;
    this.canvas.style.height = `${this.cssHeight}px`;
    this.camera.aspect = this.cssWidth / this.cssHeight;
    this.camera.updateProjectionMatrix();
    this.resizeTargets(this.width, this.height);
  }

  private resizeTargets(width: number, height: number): void {
    this.sceneTarget.setSize(width, height);
    const bloomScale = QUALITY_PROFILES[this.quality].bloomScale;
    const bw = Math.max(2, Math.round(width * bloomScale));
    const bh = Math.max(2, Math.round(height * bloomScale));
    this.brightTarget.setSize(bw, bh); this.blurTarget.setSize(bw, bh); this.bloomTarget.setSize(bw, bh);
  }

  updateParameters(p: Parameters): void {
    this.camera.fov = p.cameraFov;
    this.camera.updateProjectionMatrix();
    const currentDistance = this.camera.position.length();
    if (Math.abs(currentDistance - p.cameraDistance) > 0.3 && !this.cameraTween) this.camera.position.setLength(p.cameraDistance);
    const u = this.rayMaterial.uniforms;
    u.uFov.value = p.cameraFov; u.uStepSize.value = p.integrationStep; u.uMaximumSteps.value = Math.round(p.maximumSteps);
    u.uDiskInner.value = p.diskInnerRadius; u.uDiskOuter.value = p.diskOuterRadius; u.uDiskTemperature.value = p.diskTemperature;
    u.uEmissivityFalloff.value = p.emissivityFalloff; u.uAccretionRate.value = p.accretionRate; u.uTurbulenceScale.value = p.turbulenceScale;
    u.uTurbulenceStrength.value = p.turbulenceStrength; u.uDiskRotationSpeed.value = p.diskRotationSpeed; u.uStarDensity.value = p.starDensity;
    u.uGalaxyIntensity.value = p.galaxyIntensity;
    this.brightMaterial.uniforms.uThreshold.value = p.bloomThreshold;
    this.blurMaterial.uniforms.uRadius.value = p.bloomRadius;
    const c = this.compositeMaterial.uniforms;
    c.uExposure.value = p.exposure; c.uBloomStrength.value = p.bloomStrength; c.uChromatic.value = p.chromaticAberration;
    c.uVignette.value = p.vignette; c.uGrain.value = p.filmGrain;
  }

  setDebugView(view: number): void { this.rayMaterial.uniforms.uDebugView.value = Math.max(0, Math.min(9, Math.round(view))); }
  setSeed(seed: number): void { this.rayMaterial.uniforms.uSeed.value = seed; }

  applyPreset(name: string, immediate = false): void {
    const values = PRESETS[name] ?? PRESETS.observer!;
    const end = new THREE.Vector3(...values);
    if (immediate) {
      this.camera.position.copy(end); this.camera.lookAt(0,0,0); this.controls.update(); return;
    }
    this.cameraTween = { start: this.camera.position.clone(), end, startTime: performance.now(), duration: 1150, preset: name };
  }

  private updateCamera(now: number, cinematic: boolean): void {
    if (this.cameraTween) {
      const t = Math.min(1, (now - this.cameraTween.startTime) / this.cameraTween.duration);
      const eased = t * t * (3 - 2 * t);
      this.camera.position.lerpVectors(this.cameraTween.start, this.cameraTween.end, eased);
      this.camera.lookAt(0,0,0);
      if (t >= 1) this.cameraTween = null;
    } else if (cinematic && now >= this.cinematicSuspendUntil) {
      const t = ((now - this.cinematicStartedAt) % 60_000) / 60_000;
      this.cinematicCurve.getPointAt(t, this.camera.position);
      const roll = Math.sin(t * Math.PI * 4.0) * 0.04;
      this.camera.up.set(Math.sin(roll), Math.cos(roll), 0);
      this.camera.lookAt(0,0,0);
    } else {
      this.controls.update();
    }
  }

  render(time: number, cinematic: boolean, output: any = null): void {
    if (this.shaderFailure) return;
    this.updateCamera(performance.now(), cinematic);
    this.updateCameraUniforms();
    this.renderPipeline(this.width, this.height, time, output);
  }

  private updateCameraUniforms(): void {
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    const right = new THREE.Vector3().crossVectors(forward, this.camera.up).normalize();
    const up = new THREE.Vector3().crossVectors(right, forward).normalize();
    const u = this.rayMaterial.uniforms;
    u.uCameraPosition.value.copy(this.camera.position);
    u.uCameraForward.value.copy(forward);
    u.uCameraRight.value.copy(right);
    u.uCameraUp.value.copy(up);
  }

  private pass(material: any, target: any): void {
    this.quad.material = material;
    this.renderer.setRenderTarget(target);
    this.renderer.clear();
    this.renderer.render(this.quadScene, this.quadCamera);
  }

  private renderPipeline(width: number, height: number, time: number, output: any): void {
    this.rayMaterial.uniforms.uResolution.value.set(width, height);
    this.rayMaterial.uniforms.uTime.value = time;
    this.pass(this.rayMaterial, this.sceneTarget);
    this.brightMaterial.uniforms.uInput.value = this.sceneTarget.texture;
    this.pass(this.brightMaterial, this.brightTarget);
    this.blurMaterial.uniforms.uInput.value = this.brightTarget.texture;
    this.blurMaterial.uniforms.uDirection.value.set(1 / this.brightTarget.width, 0);
    this.pass(this.blurMaterial, this.blurTarget);
    this.blurMaterial.uniforms.uInput.value = this.blurTarget.texture;
    this.blurMaterial.uniforms.uDirection.value.set(0, 1 / this.blurTarget.height);
    this.pass(this.blurMaterial, this.bloomTarget);
    const c = this.compositeMaterial.uniforms;
    c.uScene.value = this.sceneTarget.texture; c.uBloom.value = this.bloomTarget.texture; c.uResolution.value.set(width, height); c.uTime.value = time;
    this.pass(this.compositeMaterial, output);
    this.renderer.setRenderTarget(null);
  }

  async capture(width: number, height: number, time: number): Promise<Blob> {
    width = Math.max(64, Math.min(4096, Math.round(width)));
    height = Math.max(64, Math.min(4096, Math.round(height)));
    const old = { width: this.width, height: this.height };
    this.resizeTargets(width, height);
    this.updateCameraUniforms();
    const target = makeTarget(width, height, THREE.UnsignedByteType);
    this.renderPipeline(width, height, time, target);
    const pixels = new Uint8Array(width * height * 4);
    this.renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
    target.dispose();
    this.resizeTargets(old.width, old.height);
    const flipped = new Uint8ClampedArray(pixels.length);
    const row = width * 4;
    for (let y = 0; y < height; y++) flipped.set(pixels.subarray((height - 1 - y) * row, (height - y) * row), y * row);
    const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("2D canvas unavailable for PNG encoding.");
    context.putImageData(new ImageData(flipped, width, height), 0, 0);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("PNG encoding failed.")), "image/png"));
  }

  async isOutputVisible(time: number): Promise<boolean> {
    const target = makeTarget(16, 16, THREE.UnsignedByteType);
    this.renderPipeline(this.width, this.height, time, target);
    const pixels = new Uint8Array(16 * 16 * 4);
    this.renderer.readRenderTargetPixels(target, 0, 0, 16, 16, pixels);
    target.dispose();
    let sum = 0;
    for (let i = 0; i < pixels.length; i += 4) sum += pixels[i]! + pixels[i + 1]! + pixels[i + 2]!;
    return sum / (16 * 16) > 1.5;
  }

  getStats(): RenderStats {
    const gl = this.renderer.getContext();
    return {
      width: this.width, height: this.height, dpr: this.dpr, renderScale: this.renderScale,
      hdr: this.targetType === THREE.HalfFloatType, webgl2: true,
      renderer: String(gl.getParameter(gl.RENDERER) ?? "WebGL 2")
    };
  }

  getDistance(): number { return this.camera.position.length(); }
}
